import { throttle } from '../utils/utilities';

const CURRENT = 'is-current';

/**
 * Высота липкой шапки — ей пользуются и якоря оглавления, и сам сайдбар.
 * Считаем один раз и на ресайзе: шапка меняет высоту на брейкпоинтах.
 */
const trackHeaderHeight = (header) => {
    const update = () => {
        document.documentElement.style.setProperty('--site-header-height', `${header.offsetHeight}px`);
    };

    update();

    window.addEventListener('resize', throttle(update, 200), { passive: true });
};

/**
 * Подсветка текущего раздела. Считаем по позиции заголовков, а не через
 * IntersectionObserver: короткие разделы не успевают пересечь порог,
 * и подсветка перескакивает через пункт.
 */
const trackHeadings = (links, headings) => {
    const update = () => {
        const line = window.scrollY + (parseInt(getComputedStyle(document.documentElement).getPropertyValue('--site-header-height'), 10) || 80) + 24;

        let index = 0;

        headings.forEach((heading, i) => {
            if (heading.getBoundingClientRect().top + window.scrollY <= line) {
                index = i;
            }
        });

        // У конца страницы последние разделы до линии уже не доедут —
        // короткий финал иначе никогда не подсветится.
        if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) {
            index = headings.length - 1;
        }

        links.forEach((link, i) => {
            link.classList.toggle(CURRENT, i === index);
        });
    };

    update();

    window.addEventListener('scroll', throttle(update, 100), { passive: true });
};

/**
 * Переход по оглавлению. Свой обработчик на фазе перехвата: общий
 * обработчик якорей считает позицию до того, как раскладка встала, и
 * недокручивает на пару сотен пикселей. scrollIntoView уважает
 * scroll-margin-top заголовка, так что смещение под липкую шапку
 * задано в CSS и живёт в одном месте.
 */
const trackClicks = (links, headings) => {
    links.forEach((link, index) => {
        link.addEventListener(
            'click',
            (event) => {
                event.preventDefault();
                event.stopImmediatePropagation();

                headings[index].scrollIntoView({
                    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
                    block: 'start',
                });
            },
            true
        );
    });
};

const copyLink = (button) => {
    const status = document.querySelector('[data-copy-status]');
    const url = button.getAttribute('data-copy-link');

    button.addEventListener('click', () => {
        const done = () => {
            if (status) {
                status.textContent = button.getAttribute('data-copied-label') || '';

                setTimeout(() => {
                    status.textContent = '';
                }, 2400);
            }
        };

        if (navigator.clipboard) {
            navigator.clipboard.writeText(url).then(done);

            return;
        }

        // Старые браузеры и http-контекст: clipboard недоступен.
        const field = document.createElement('input');

        field.value = url;
        document.body.appendChild(field);
        field.select();
        document.execCommand('copy');
        document.body.removeChild(field);
        done();
    });
};

/**
 * Поиск в тулбаре: кнопка раскрывает поле влево, повторный клик по пустому
 * полю сворачивает его, по заполненному — отправляет форму. Enter в поле
 * отправляет её сам: другой кнопки-submit в форме нет. Esc сворачивает.
 * В шапке результатов форма приходит уже раскрытой, там работает только
 * крестик — он чистит поле и возвращает фокус.
 */
const searchForm = (form) => {
    const field = form.querySelector('.blog-archive__search-field');
    const toggle = form.querySelector('[data-blog-search-toggle]');
    const clear = form.querySelector('[data-blog-search-clear]');

    if (!field) {
        return;
    }

    // Крестик живёт по содержимому поля: пустое поле чистить нечем.
    const syncClear = () => {
        if (clear) {
            clear.hidden = field.value === '';
        }
    };

    const setOpen = (open) => {
        form.classList.toggle('is-open', open);

        if (toggle) {
            toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        }
    };

    syncClear();
    field.addEventListener('input', syncClear);

    if (toggle) {
        if (field.value.trim() !== '') {
            setOpen(true);
        }

        toggle.addEventListener('click', () => {
            if (!form.classList.contains('is-open')) {
                setOpen(true);
                field.focus();

                return;
            }

            if (field.value.trim() === '') {
                setOpen(false);

                return;
            }

            form.requestSubmit ? form.requestSubmit() : form.submit();
        });

        field.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                field.value = '';
                syncClear();
                setOpen(false);
                toggle.focus();
            }
        });
    }

    if (clear) {
        clear.addEventListener('click', () => {
            field.value = '';
            syncClear();
            field.focus();
        });
    }

    form.addEventListener('submit', (event) => {
        if (field.value.trim() === '') {
            event.preventDefault();
            field.focus();
        }
    });
};

export default function Blog() {
    document.querySelectorAll('[data-blog-search]').forEach(searchForm);

    const header = document.querySelector('[data-header]');

    if (header) {
        trackHeaderHeight(header);
    }

    const toc = document.querySelector('[data-article-toc]');

    if (toc) {
        const links = Array.from(toc.querySelectorAll('.article__toc-link'));

        const headings = links
            .map((link) => document.getElementById(decodeURIComponent(link.getAttribute('href').slice(1))))
            .filter(Boolean);

        if (headings.length === links.length && headings.length > 0) {
            trackHeadings(links, headings);
            trackClicks(links, headings);
        }
    }

    const copy = document.querySelector('[data-copy-link]');

    if (copy) {
        copyLink(copy);
    }
}
